import { useMutation, useQueryClient } from "@tanstack/react-query";
import { use } from "react";
import {
  EditCardViewResponse,
  ListCardsResponseWithProjectName,
} from "tng-address-collection-shared/types/apiTypes.ts";
import { useAuthentication } from "../auth/useAuthentication.ts";
import { useTranslator } from "../i18n/translator.ts";
import { SnackbarContext } from "../snackbar/SnackbarContext.ts";
import { queryKeys } from "./queryKeys.ts";
import { useInvalidateCardErrorsCache } from "./useInvalidateCardErrorsCache.ts";

export const useConfirmCardContactDetails = ({
  cardId,
  projectId,
}: {
  cardId: string;
  projectId: string;
}) => {
  const translator = useTranslator();
  const { getToken } = useAuthentication();
  const queryClient = useQueryClient();
  const invalidateCardErrorsCache = useInvalidateCardErrorsCache();
  const { displayError, displaySuccess } = use(SnackbarContext);

  const { error, isPending, mutate, isError, reset } = useMutation({
    mutationFn: async (): Promise<Response> => {
      const token = await getToken();
      const response = await fetch(
        `/api/projects/${projectId}/cards/${cardId}/manualContactDetailsConfirmation`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      if (!response.ok) {
        throw new Error("Marking the contact details correct failed");
      }

      return response;
    },
    mutationKey: ["confirmCardContactDetails", cardId],
    onSuccess: () => {
      queryClient.setQueriesData<EditCardViewResponse>(
        { queryKey: queryKeys.cardEditViewsOfCard(cardId) },
        (old) => {
          if (!old) return old;

          return {
            ...old,
            hasContactDetailsWarning: false,
            contactDetailsWarning: undefined,
          };
        },
      );

      queryClient.setQueryData<ListCardsResponseWithProjectName>(
        queryKeys.cardsInProject(projectId),
        (old) =>
          old
            ? {
                ...old,
                items: old.items.map((card) =>
                  card.id === cardId
                    ? { ...card, hasContactDetailsWarning: false }
                    : card,
                ),
              }
            : old,
      );

      invalidateCardErrorsCache();
      displaySuccess(translator("contactDetailsMarkedCorrect"));
    },
    onError: () => {
      displayError(translator("errorMarkingContactDetailsCorrect"));
    },
  });

  return { mutate, isPending, error, isError, reset };
};
